import { useCallback } from "react";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DateRangeFilterMenuItemType,
  DateFilterValue,
  dateOperatorToLabel,
} from "../FilterMenu.model";
import {
  isBetweenFilter,
  isGreaterThanOrEqualFilter,
} from "../FieldFilter.model";
import { DateRangeCalendar } from "../DateRangeCalendar";
import { DateSingleCalendar } from "../DateSingleCalendar";
import { formatBound } from "../dateBoundValue";
import styles from "../FilterMenu.module.scss";

function getOperator(value: DateFilterValue) {
  if (isBetweenFilter(value)) return "between" as const;
  if (isGreaterThanOrEqualFilter(value)) return "gte" as const;
  return "lte" as const;
}

interface DateRangeActiveFilterItemProps<Field extends string>
  extends DateRangeFilterMenuItemType<Field> {
  value: DateFilterValue;
  size: "1" | "2" | "3";
  viewOnly: boolean;
  onChange: (item: DateRangeFilterMenuItemType<Field>) => void;
}

export function DateRangeActiveFilterItem<Field extends string>({
  size,
  viewOnly,
  onChange,
  ...item
}: DateRangeActiveFilterItemProps<Field>) {
  const operator = getOperator(item.value);

  const handleRangeChange = useCallback(
    (from: number, to: number) =>
      onChange({
        ...item,
        value: { operator: "between", value: [from, to] },
      }),
    [item, onChange]
  );

  const handleSingleChange = useCallback(
    (date: number) =>
      onChange({
        ...item,
        value: { operator: operator as "gte" | "lte", value: date },
      }),
    [item, onChange, operator]
  );

  const handleClear = useCallback(
    () => onChange({ ...item, value: undefined }),
    [item, onChange]
  );

  const pillClassName = `rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`;

  return (
    <Flex gap={"1px"} className={styles.activeFilter} align="center">
      <div data-accent-color="gray" className={pillClassName}>
        {item.icon}
        {item.label}
      </div>

      <div
        data-accent-color="gray"
        className={pillClassName}
      >
        {dateOperatorToLabel[operator]}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger disabled={viewOnly} asChild>
          <div
            data-accent-color="gray"
            className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
              viewOnly ? "pointer-events-none" : ""
            } shrink`}
          >
            <Text truncate>
              {isBetweenFilter(item.value)
                ? `${formatBound(item.value.value[0])} - ${formatBound(item.value.value[1])}`
                : formatBound(item.value.value as number)}
            </Text>
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-auto p-0">
          {isBetweenFilter(item.value) ? (
            <DateRangeCalendar
              from={item.value.value[0]}
              to={item.value.value[1]}
              onChange={handleRangeChange}
            />
          ) : (
            <DateSingleCalendar
              date={item.value.value as number}
              bound={operator === "lte" ? "end" : "start"}
              onChange={handleSingleChange}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {!viewOnly && (
        <IconButton
          variant="soft"
          color="gray"
          size={size}
          onClick={handleClear}
        >
          <Cross1Icon />
        </IconButton>
      )}
    </Flex>
  );
}
